import React from 'react'
import { Box, CircularProgress, Typography, Button, Paper, Alert, Stack, Divider, Chip, TextField, Link, InputAdornment, IconButton } from '@mui/material'
import { EmailOutlined, LockOutlined, Visibility, VisibilityOff } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../AuthProvider'
import { useAppDispatch } from '../../../store/store'
import { loginUser } from '../authSlice'
import '../../admin/pages/AdminLogin.css'
import './Login.css'
import '../../../shared/components/fonts.css'

/**
 * Native Login Page
 */
const Login: React.FC = () => {
  const { logout, authenticated, user } = useAuth()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  
  const [identifier, setIdentifier] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [showPassword, setShowPassword] = React.useState(false)

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255, 255, 255, 0.02)',
      borderRadius: '12px',
      transition: 'all 0.2s ease-in-out',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.12)',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: 'var(--login-accent)',
        borderWidth: '2px',
        boxShadow: '0 0 16px rgba(122, 167, 255, 0.15)',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.5)',
      '&.Mui-focused': {
        color: 'var(--login-accent)',
      }
    },
    '& .MuiInputBase-input': {
      color: 'var(--login-text)',
    }
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!identifier || !password) {
      setError(t('auth.loginEmpty', 'Bitte Benutzername und Passwort eingeben.'))
      return
    }
    setError(null)
    setLoading(true)
    try {
      await dispatch(loginUser({ identifier, password })).unwrap()
      // redirect to original destination or profile
      const origin = (location.state as any)?.from?.pathname || '/profile'
      navigate(origin, { replace: true })
    } catch (err: any) {
      console.error('Login failed:', err)
      setError(err || t('auth.loginFailed', 'Login fehlgeschlagen. Bitte erneut versuchen.'))
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    setError(null)
    setLoading(true)
    try {
      await logout()
    } catch (err) {
      console.error('Logout failed:', err)
      setError(t('auth.logoutFailed', 'Logout fehlgeschlagen.'))
      setLoading(false)
    }
  }

  return (
    <Box
      className="kavithai-login-root"
    >
      <Box className="kavithai-login-grid">
        <Box className="kavithai-login-hero">
          <Typography
            variant="overline"
            className="kavithai-login-overline"
          >
            Kavithai.de
          </Typography>
          <Typography
            variant="h3"
            className="kavithai-gradient-text kavithai-login-title"
          >
            {t('auth.loginHeadline', 'Dein Schreibraum für tamilische Poesie')}
          </Typography>
          <Typography className="kavithai-login-subtitle">
            {t('auth.loginSubline', 'Verwalte deine Gedichte, setze Veröffentlichungen und halte alles an einem Ort.')}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" className="kavithai-login-chips">
            <Chip label={t('auth.loginChip1', 'Tamil Keyboard')} size="small" />
            <Chip label={t('auth.loginChip2', 'Rich Editor')} size="small" />
            <Chip label={t('auth.loginChip3', 'Admin Control')} size="small" />
          </Stack>
        </Box>

        <Paper elevation={0} className="kavithai-login-card" sx={{ p: { xs: 3, sm: 4, md: 5 } }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" className="kavithai-login-card-title" gutterBottom sx={{ fontSize: '1.75rem', mb: 1 }}>
                {t('nav.login', 'Willkommen zurück')}
              </Typography>
              <Typography variant="body1" className="kavithai-login-card-subtitle" sx={{ fontSize: '0.95rem' }}>
                {authenticated
                  ? `${t('auth.alreadyLoggedIn', 'Du bist bereits angemeldet als')} ${user?.username || user?.email || 'User'}.`
                  : t('auth.loginPrompt', 'Bitte logge dich ein, um fortzufahren.')}
              </Typography>
            </Box>
            <Divider className="kavithai-login-divider" sx={{ opacity: 0.5, my: 1 }} />

            {authenticated ? (
              <Button
                variant="outlined"
                onClick={handleLogout}
                disabled={loading}
                className="kavithai-login-secondary-btn"
              >
                {t('nav.logout', 'Logout')}
              </Button>
            ) : (
              <form onSubmit={handleLogin} style={{ width: '100%' }}>
                <Stack spacing={2.5}>
                  <TextField
                    label={t('auth.identifierLabel', 'Benutzername oder E-Mail')}
                    variant="outlined"
                    fullWidth
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    disabled={loading}
                    sx={textFieldSx}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailOutlined sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box>
                    <TextField
                      label={t('auth.passwordLabel', 'Passwort')}
                      type={showPassword ? 'text' : 'password'}
                      variant="outlined"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      sx={textFieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlined sx={{ color: 'rgba(255, 255, 255, 0.4)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              sx={{ color: 'rgba(255, 255, 255, 0.4)' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />
                    <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                      <Link 
                        component={RouterLink} 
                        to="/forgot-password" 
                        variant="body2" 
                        sx={{ 
                          color: 'var(--login-accent)', 
                          textDecoration: 'none',
                          fontWeight: 500,
                          transition: 'color 0.2s',
                          '&:hover': { color: 'var(--login-accent-2)' }
                        }}
                      >
                        {t('auth.forgotPassword', 'Passwort vergessen?')}
                      </Link>
                    </Box>
                  </Box>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={loading}
                    className="kavithai-login-primary-btn"
                    sx={{ mt: 1, py: 1.5, borderRadius: '8px', fontSize: '1rem' }}
                  >
                    {loading ? <CircularProgress size={24} color="inherit" /> : t('nav.login', 'Anmelden')}
                  </Button>
                </Stack>
              </form>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </Paper>
      </Box>
    </Box>
  )
}

export default Login
